import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  ExitToApp as ExitIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const CycleSuccessDialog = ({
  open,
  onClose,
  data,
  type = "create",
  autoClose = true,
  autoCloseDelay = 3000,
}) => {
  const [countdown, setCountdown] = useState(autoCloseDelay / 1000);

  useEffect(() => {
    if (open && autoClose) {
      setCountdown(autoCloseDelay / 1000);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, autoClose, autoCloseDelay, onClose]);

  // Si no hay datos o no está abierto, no renderizar
  if (!open) return null;

  const isCreate = type === "create";
  const title = isCreate ? "¡Ciclo Creado Exitosamente!" : "¡Ciclo Cerrado Exitosamente!";
  const subtitle = isCreate
    ? "El vehículo ha sido registrado en el parqueadero"
    : "El vehículo ha salido del parqueadero";
  const iconColor = isCreate ? "#2e7d32" : "#ed6c02";
  const gradient = isCreate
    ? "linear-gradient(135deg, #09702d 0%, #0a8511 100%)"
    : "linear-gradient(135deg, #e65100 0%, #ed6c02 100%)";
  const bgColor = isCreate ? "#f1f8e9" : "#fff3e0";
  const borderColor = isCreate ? "#e8f5e9" : "#ffe0b2";
  const IconComponent = isCreate ? AddIcon : ExitIcon;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  // Datos por defecto si no hay data
  const displayData = data || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          background: gradient,
          p: 3,
          pb: 4,
          textAlign: "center",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          component="img"
          src={logoImage}
          alt="GoPass"
          sx={{
            height: 50,
            width: "auto",
            objectFit: "contain",
            mb: 2,
            filter: "brightness(0) invert(1)",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="bold" color="rgba(255,255,255,0.85)" sx={{ mt: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.85)">
          {subtitle}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "12px",
            border: `1px solid ${borderColor}`,
            backgroundColor: bgColor,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <CarIcon sx={{ color: iconColor }} />
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                  {isCreate ? "DETALLES DE ENTRADA" : "DETALLES DE SALIDA"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Placa
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {displayData.plate || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Terminal
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {displayData.idterminal || displayData.terminal || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {isCreate ? "Fecha de Entrada" : "Fecha de Salida"}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {isCreate
                  ? formatDate(displayData.enter_at || displayData.date)
                  : formatDate(displayData.out_at || displayData.exitDate)}
              </Typography>
            </Grid>

            {!isCreate && displayData.observation && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Observación
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {displayData.observation}
                </Typography>
              </Grid>
            )}

            {isCreate && displayData.observation && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Observación
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {displayData.observation}
                </Typography>
              </Grid>
            )}

            {isCreate && displayData.host && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Host / Cámara
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {displayData.host}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: isCreate ? "rgba(46, 125, 50, 0.08)" : "rgba(237, 108, 2, 0.08)",
                  border: `1px solid ${
                    isCreate ? "rgba(46, 125, 50, 0.2)" : "rgba(237, 108, 2, 0.2)"
                  }`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <IconComponent sx={{ color: iconColor, fontSize: 20 }} />
                <Typography variant="body2" color={iconColor} fontWeight="medium">
                  {isCreate
                    ? "Vehículo registrado exitosamente en el parqueadero"
                    : "Salida registrada exitosamente del parqueadero"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {autoClose && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Cerrando en {countdown} segundos...
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <MDButton
            variant="contained"
            color="primary"
            onClick={onClose}
            startIcon={<CheckCircleIcon />}
            sx={{
              borderRadius: "8px",
              fontWeight: "bold",
              px: 4,
              height: "48px",
            }}
          >
            Aceptar
          </MDButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CycleSuccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  type: PropTypes.oneOf(["create", "close"]),
  autoClose: PropTypes.bool,
  autoCloseDelay: PropTypes.number,
};

CycleSuccessDialog.defaultProps = {
  data: null,
  type: "create",
  autoClose: true,
  autoCloseDelay: 3000,
};

export default CycleSuccessDialog;
